'use client';

import { ChevronDownIcon } from '@heroicons/react/16/solid';
import { useLocale, useTranslations } from 'next-intl';

import { Button, HeroIcon } from '@/components/atoms';
import { Dropdown } from '@/components/molecules';
import { useRouter, usePathname } from '@/i18n/routing';
import { useConsent } from '@/lib/contexts/consent/ConsentProvider';
import { cn } from '@/lib/utils/tailwind';

import type { DropdownProps } from '@/components/molecules';

import type { Language } from '@/lib/api/types';

export type LanguageSelectorProps = DropdownProps & {
  openMenu: boolean;
  action: () => void;
  languages: Language[];
  placement: 'start' | 'center' | 'end';
};

export const LanguageSelector = ({
  openMenu,
  action,
  languages,
  dir,
  placement = 'start',
}: LanguageSelectorProps) => {
  const t = useTranslations();
  const router = useRouter();
  const pathname = usePathname();
  const locale = useLocale();
  const consentContext = useConsent();

  const updateLanguage = (lang: string) => {
    const code = lang.toLowerCase();
    consentContext.setCookiesLanguage(code);
    document.cookie = `NEXT_LOCALE=${code}; path=/; max-age=31536000; SameSite=Lax; Secure`;
    document.documentElement.lang = code;
    router.replace(pathname, { locale: code });
  };

  return (
    <Dropdown onOpenChange={action} open={openMenu} dir={dir}>
      <Dropdown.Trigger
        as={Button}
        variant="secondary"
        size="sm"
        icon={
          <HeroIcon
            icon={ChevronDownIcon}
            size="small"
            className={cn(
              'size-3.5 transition-transform duration-200 ease-in-out',
              openMenu && 'rotate-180',
            )}
          />
        }
      >
        {locale.toUpperCase()}
      </Dropdown.Trigger>
      <Dropdown.Content
        align={placement}
        className={cn('z-2000')}
        title={t('B2B_LANGUAGE_SELECTOR_TITLE')}
      >
        {languages.map(language => {
          const isCurrent = locale === language.shortcode;
          return (
            <Dropdown.Item
              key={language.shortcode}
              onClick={() => updateLanguage(language.shortcode)}
              onKeyDown={() => updateLanguage(language.shortcode)}
              variant="secondary"
              className={cn('flex justify-center')}
              isActive={isCurrent}
            >
              {language.name}
            </Dropdown.Item>
          );
        })}
      </Dropdown.Content>
    </Dropdown>
  );
};
